import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Alert, Progress, Space, Typography } from 'antd';
import snAiReviewService, { ISnAiRunStatus } from '../../../services/cmdb/snAiReview/snAiReview.service';

const { Text } = Typography;

const POLL_RUNNING_MS = 15000;
const POLL_IDLE_MS = 60000;

const fmtTime = (iso?: string | null) => (iso ? new Date(iso).toLocaleString() : '—');
const fmtNumber = (n?: number | null) => (n ?? 0).toLocaleString();

const fmtDuration = (ms: number) => {
  if (!Number.isFinite(ms) || ms <= 0) return '';
  const totalMinutes = Math.round(ms / 60000);
  const h = Math.floor(totalMinutes / 60);
  const m = totalMinutes % 60;
  return h ? `${h}h ${m}m` : `${m}m`;
};

interface IRunStatusProps {
  /** Change this value to refresh immediately (e.g. right after Run Batch Now). */
  refreshKey: number;
  onRunningChange?: (running: boolean) => void;
  /** Called once when a run that was in progress finishes, so the review list can reload. */
  onRunFinished?: () => void;
}

// Status of the Software Normalization AI background run: progress while running, the last run,
// and when the nightly run starts next. Polls every 15s while a run is in progress, every 60s otherwise.
const RunStatus: React.FC<IRunStatusProps> = ({ refreshKey, onRunningChange, onRunFinished }) => {
  const [status, setStatus] = useState<ISnAiRunStatus | null>(null);
  const wasRunning = useRef(false);

  const load = useCallback(async () => {
    try {
      const s = await snAiReviewService.getRunStatus();
      setStatus(s);
      onRunningChange?.(s.running);
      if (wasRunning.current && !s.running) onRunFinished?.();
      wasRunning.current = s.running;
    } catch { /* handled by request interceptor */ }
  }, [onRunningChange, onRunFinished]);

  useEffect(() => { load(); }, [load, refreshKey]);

  useEffect(() => {
    const timer = setInterval(load, status?.running ? POLL_RUNNING_MS : POLL_IDLE_MS);
    return () => clearInterval(timer);
  }, [load, status?.running]);

  if (!status) return null;

  const run = status.current_run;
  const counts = (
    <Text type="secondary">
      Waiting for AI: {fmtNumber(status.queued)} · In review queue: {fmtNumber(status.review_queue)}
    </Text>
  );
  const nightly = status.scheduler_enabled ? (
    <Text type="secondary">
      Next nightly run: {fmtTime(status.next_scheduled_run)}
      {status.next_scheduled_run && ` (in ${fmtDuration(new Date(status.next_scheduled_run).getTime() - Date.now()) || 'under a minute'})`}
    </Text>
  ) : (
    <Text type="secondary">Nightly run: off (SN AI Settings)</Text>
  );

  if (status.running && run) {
    const total = run.batch_size ?? 0;
    const percent = total ? Math.min(100, Math.round((run.processed / total) * 100)) : 0;
    const elapsed = Date.now() - new Date(run.started_at).getTime();
    const remaining = run.processed > 0 && total ? (elapsed / run.processed) * (total - run.processed) : NaN;
    return (
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message={
          <Space direction="vertical" style={{ width: '100%' }} size={4}>
            <Text strong>
              AI normalization running ({run.trigger === 'manual' ? 'started manually' : 'nightly run'}):{' '}
              {total ? `${fmtNumber(run.processed)} of ${fmtNumber(total)} records` : 'loading the batch…'}
              {fmtDuration(remaining) && ` · about ${fmtDuration(remaining)} left`}
            </Text>
            <Progress percent={percent} size="small" status="active" />
            <Text type="secondary">
              Started {fmtTime(run.started_at)}
              {run.failed_groups > 0 && ` · ${run.failed_groups} group(s) failed and went back to the queue`}
            </Text>
            {counts}
          </Space>
        }
      />
    );
  }

  const last = status.last_run;
  return (
    <Alert
      type={last?.error ? 'warning' : 'success'}
      showIcon
      style={{ marginBottom: 16 }}
      message={
        <Space direction="vertical" size={2}>
          <Text strong>
            {last
              ? `Last run finished ${fmtTime(last.finished_at)}: ${fmtNumber(last.processed)} records (${
                  last.trigger === 'manual' ? 'started manually' : 'nightly run'
                })${last.error ? ` — stopped with an error: ${last.error}` : ''}`
              : 'No AI normalization run since the API last restarted.'}
          </Text>
          {nightly}
          {counts}
          {status.marked_processing > 0 && (
            <Text type="warning">
              {fmtNumber(status.marked_processing)} records are marked Processing but no run is active; they go back to
              the queue when the next run starts.
            </Text>
          )}
        </Space>
      }
    />
  );
};

export default RunStatus;
